
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useProductStatistics = (orderId: string, searchQuery?: string, posStatusFilter?: 'all' | 'new' | 'existing') => {
  return useQuery({
    queryKey: ['product-statistics', orderId, searchQuery, posStatusFilter],
    queryFn: async () => {
      console.log('🔄 Fetching product statistics for order:', orderId);
      
      // Base query for products in this order
      let baseQuery = supabase
        .from('products')
        .select('id, barcode, name, boxes, square_item_id, square_variation_id', { count: 'exact' })
        .eq('order_id', orderId);

      // Apply search filter if provided
      if (searchQuery && searchQuery.trim()) {
        baseQuery = baseQuery.or(`name.ilike.%${searchQuery}%,barcode.ilike.%${searchQuery}%`);
      }

      const { data: products, count: totalCount, error } = await baseQuery;
      
      if (error) {
        console.error('❌ Error fetching product statistics:', error);
        throw error;
      }

      console.log('📊 Statistics query returned:', totalCount, 'products');

      // Check POS status for all products by looking up in product_catalog
      const productsWithPosStatus = await Promise.all(
        (products || []).map(async (product) => {
          try {
            const { data: catalogData } = await supabase
              .from('product_catalog')
              .select('name')
              .eq('barcode', product.barcode)
              .maybeSingle();

            return {
              ...product,
              posStatus: catalogData ? 'found' as const : 'not-found' as const
            };
          } catch (error) {
            console.error('Error checking POS status for product:', product.id, error);
            return {
              ...product,
              posStatus: 'not-found' as const
            };
          }
        })
      );

      // Calculate statistics
      const inPosCount = productsWithPosStatus.filter(p => p.posStatus === 'found').length;
      const notInPosCount = productsWithPosStatus.filter(p => p.posStatus === 'not-found').length;
      const totalBoxes = productsWithPosStatus.reduce((sum, p) => sum + p.boxes, 0);

      // Apply POS status filter for filtered counts
      let filteredProducts = productsWithPosStatus;
      if (posStatusFilter === 'new') {
        filteredProducts = productsWithPosStatus.filter(p => p.posStatus === 'not-found');
      } else if (posStatusFilter === 'existing') {
        filteredProducts = productsWithPosStatus.filter(p => p.posStatus === 'found');
      }

      const filteredCount = filteredProducts.length;
      const filteredBoxes = filteredProducts.reduce((sum, p) => sum + p.boxes, 0);

      const stats = {
        totalProducts: totalCount || 0,
        inPosCount,
        notInPosCount,
        totalBoxes,
        filteredCount,
        filteredBoxes,
        // For compatibility with existing components
        totalCount: totalCount || 0,
        newCount: notInPosCount,
        existingCount: inPosCount
      };

      console.log('✅ Product statistics calculated:', stats);
      return stats;
    },
    enabled: !!orderId,
    staleTime: 30000, // Cache for 30 seconds since statistics change less frequently
    refetchOnWindowFocus: false
  });
};
